import { Injectable } from "@angular/core";
import { Subject } from 'rxjs';

@Injectable({
    providedIn: "root"
})
export class EditorModelService {
    private chartColors: Array<string> = [
        "#0061AF",
        "#652D90",
        "#008641",
        "#F15A22",
        "#BA006F",
        "#0083CA",
        "#CC99FF",
        "#9ACA3C",
        "#F58220",
        "#F5A4C7",
        "#7BAFDE",
        "#9B6DCC",
        "#3A7224",
        "#FED09E",
        "#00B6F1",
        "#6F60AA",
        "#DDE89A",
        "#FCAF17",
        "#8ED8F8",
        "#CBDB2A",
        "#FBB034",
        "#00ACD4",
        "#B5AF75",
        "#007B8D",
        "#669156",
        "#00B3B5",
        "#8C9B1B",
        "#ADE0EE",
        "#8FCEA5",
        "#9FCBED",
        "#00AE4D",
        "#72CDF4",
        "#78A22F",
        "#009DDC",
        "#8FCEA5",
        "#009AC7"
    ];

    private custCatColorMap: any = new Map();
    private chatColors: Array<string>;
    private percentages: Array<any> = [];
    private categories: Array<string> = [];
    private categoryTargetPercent: Array<any> = [];
    private totalTargetPercent: number;
    private globalSettings: any;
    private allocationList: Array<any> = [];
    parametersChanged = new Subject<any>();
    targetMinMaxChanged: boolean = false;
    refreshData = new Subject<any>();

    createCustCatMap(securityModelAllocationList: Array<any>, decimal?: number, field?: string) {
        this.allocationList = securityModelAllocationList || [];
        let colorIndex = 0, colorCode, target, category, tp, tgtVal;
        let categoryPercentageMap: any;
        decimal = decimal ? decimal : 1;
        field = field ? field : 'targetPercentage';
        if (securityModelAllocationList && securityModelAllocationList.length > 0) {
            categoryPercentageMap = new Map();
            for (const allocation of securityModelAllocationList) {
                if (!allocation.isUIdeleted) {
                    category = allocation.customCategoryName;
                    if (!categoryPercentageMap.has(category)) {
                        target = allocation[field] ? allocation[field] : '0';
                        categoryPercentageMap.set(category, parseFloat(target));
                    } else {
                        target = categoryPercentageMap.get(category);
                        tp = !allocation[field] || allocation[field] === '' ? "0" : allocation[field];
                        target = target + parseFloat(tp);
                        categoryPercentageMap.set(category, target);
                    }
                }
            }
            this.clearAll();
            categoryPercentageMap.forEach((value: number, key: string) => {
                colorCode = key === 'Uncategorized' ? '#BABABA' : this.chartColors[colorIndex];
                tgtVal = value.toFixed(decimal);
                this.categoryTargetPercent.push({ 'category': key, 'targetPercent': tgtVal,  colorCode: colorCode});
                if (key !== 'Uncategorized') {
                    colorIndex++;
                }
                if (colorIndex >= this.chartColors.length) {
                    colorIndex = 0;
                }
            });
            this.categoryTargetPercent.sort((a, b) => (a.targetPercent < b.targetPercent) ? 1 : -1);
            this.categoryTargetPercent.forEach((item) => {
                this.percentages.push(item.targetPercent);
                this.categories.push(item.category);
                this.custCatColorMap.set(item.category, item.colorCode);
                this.chatColors.push(item.colorCode);
                this.totalTargetPercent += Number(item.targetPercent);
            });
            this.totalTargetPercent = Number(this.totalTargetPercent.toFixed(decimal));
        }
        this.custCatColorMap.set('Under-Allocated', '#93000F');
    }

    getChatColors() {
        if (!this.chatColors) {
            this.chatColors = this.chartColors;
        }
        return this.chatColors;
    }

    getColorByCategory(category: string) {
        if (category === 'Under-Allocated' && !this.custCatColorMap.get(category)) {
            this.custCatColorMap.set('Under-Allocated', '#93000F');
        }
        let returnColor = this.custCatColorMap.get(category);
        if (!returnColor) {
            this.addCategoryToMap(category);
            returnColor = this.custCatColorMap.get(category);
        }
        return returnColor;
    }

    getCategoryTargetPercent() {
        return this.categoryTargetPercent;
    }

    getPercentages() {
        return this.percentages;
    }

    getCategories() {
        return this.categories;
    }

    changedData() {
        this.parametersChanged.next(null);
    }

    getTotalTargetPercent() {
        return this.totalTargetPercent;
    }
    private addCategoryToMap(category: string) {
        let colorIdx = (this.custCatColorMap.size -1) >= this.chartColors.length ? 0 : this.custCatColorMap.size - 1;
        if (colorIdx < 0) {
            colorIdx = 0;
        }
        let selectedColor = this.chartColors[colorIdx];
        if (category === 'Uncategorized') {
            selectedColor = '#BABABA';
        }
        this.custCatColorMap.set(category, selectedColor);
    }

    clearAll() {
        this.percentages = [];
        this.categories = [];
        this.categoryTargetPercent = [];
        this.chatColors = [];
        this.custCatColorMap = new Map();
        this.totalTargetPercent = 0;
    }

    triggerRefreshData() {
        this.refreshData.next(null);
    }

    getGlobalSettings() {
        return this.globalSettings;
    }
    setGlobalSettings(globSet: any) {
        this.globalSettings = globSet;
    }

    getAllocationList(): Array<any> {
        return this.allocationList;
    }

    getGroupedAllocations(groupBy: string, decimal?: number): Array<any> {
        let grouped: Array<any> = [];
        let colorIndex = 0;
        decimal = decimal ? decimal : 1;
        const allocations = this.allocationList.filter((a: any) => !a.isUIdeleted);

        if (groupBy == "POSITION") {
            allocations.forEach((a: any) => {
                let target = a.targetPercentage ? parseFloat(a.targetPercentage) : 0;

                grouped.push({
                    category: a.securitySymbol,
                    targetPercent: target.toFixed(decimal),
                    colorCode: a.securitySymbol === 'Uncategorized' ? '#BABABA' : this.chartColors[colorIndex]
                });
                if (a.securitySymbol !== 'Uncategorized') {
                    colorIndex++;
                    if (colorIndex >= this.chartColors.length) { colorIndex = 0; }
                }
            });
        } else if (groupBy == "CUST_CAT") {
            return this.categoryTargetPercent;
        } else if (groupBy == "MSS") {
            let map = new Map<string, number>();
            allocations.forEach((a: any) => {
                const style = a.styleBoxLabel ? a.styleBoxLabel : "Uncategorized";
                const target = a.targetPercentage ? parseFloat(a.targetPercentage) : 0;
                if (map.has(style)) {
                    map.set(style, map.get(style)! + target);
                } else {
                    map.set(style, target);
                }
            });
            map.forEach((value: number, key: string) => {
                const colorCode = key === 'Uncategorized' ? '#BABABA' : this.chartColors[colorIndex];
                grouped.push({ category: key, targetPercent: value.toFixed(decimal), colorCode: colorCode });
                if (key !== 'Uncategorized') {
                    colorIndex++;
                    if (colorIndex >= this.chartColors.length) { colorIndex = 0; }
                }
            });
        } else if (groupBy == "SAC1" || groupBy == "SAC2" || groupBy == "SAC3" || groupBy == "SAC4") {
            let acField = groupBy == "SAC1" ? "acLevel1" : groupBy == "SAC2" ? "acLevel2" : groupBy == "SAC3" ? "acLevel3" : "acLevel4";
            let map = new Map<string, { value: number, label: string }>();
            allocations.forEach((a: any) => {
                const acLevel = a[acField];
                const target = a.targetPercentage ? parseFloat(a.targetPercentage) : 0;
                if (!acLevel || acLevel.length == 0) {
                    const key = a.securitySymbol;
                    if (map.has(key)) {
                        map.set(key, { value: map.get(key)!.value + target, label: key });
                    } else {
                        map.set(key, { value: target, label: key });
                    }
                } else {
                    acLevel.forEach((ac: any) => {
                        const allocation = ac.allocation / 100;
                        const code = ac.code.toString();
                        const label = ac.label;
                        if (map.has(code)) {
                            map.set(code, { value: map.get(code)!.value + (target * allocation), label: label });
                        } else {
                            map.set(code, { value: target * allocation, label: label });
                        }
                    });
                }
            });
            map.forEach((data: { value: number, label: string }, key: string) => {
                const colorCode = key === 'Uncategorized' ? '#BABABA' : this.chartColors[colorIndex];
                grouped.push({ category: data.label, targetPercent: data.value.toFixed(decimal), colorCode: colorCode });
                if (key !== 'Uncategorized') {
                    colorIndex++;
                    if (colorIndex >= this.chartColors.length) { colorIndex = 0; }
                }
            });
        }

        grouped.sort((a: any, b: any) => {
            const labelA = (a.category || '').toLowerCase();
            const labelB = (b.category || '').toLowerCase();
            return labelA.localeCompare(labelB);
        });

        return grouped;
    }
}
